// Channel bookmarks bar (shared/API.md §41.2): a horizontal strip of chips under the conversation
// header, drawn from the store and hidden when the channel has none. A link chip opens its URL in a
// new tab; a file chip opens the viewer (pictures), the browser's viewer (PDF) or downloads. «+» at
// the end (and «Agregar un marcador» in the info panel, for an empty bar) only for who may add.
// Each chip's menu (right-click, the context-menu key or Shift+F10) edits, reorders and deletes —
// each item only when permitted. Every write is optimistic in the worker; a refusal reverts and
// the toast says why.
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { data } from '../../../bridge/client';
import { AttachmentKind } from '../../../core/attachmentKind';
import { BOOKMARK_NAME_MAX, defaultBookmarkName, NO_BOOKMARK_RIGHTS, normalizeBookmarkUrl, type BookmarkRights, type ChannelBookmarkRecord } from '../../../core/channelBookmarks';
import type { BookmarkWriteOutcome } from '../../../data/features/bookmarks';
import { Icon } from '../../../design/icons';
import { t, tf } from '../../../i18n/i18n';
import { useLive } from '../../hooks';
import { Button, Dialog, ListRow } from '../../kit/Kit';
import { mediaUrls } from '../../media';
import { useShell } from '../../shell/ShellContext';
import { openLightbox } from '../attachments/Lightbox';
import { openPdf } from '../attachments/PostFiles';
import { EmojiGlyph } from '../emoji/EmojiGlyph';
import './bookmarks.css';

/** The rights for a channel, re-asked when its bookmarks change (a refresh may flip availability). */
export function useBookmarkRights(channelId: string): BookmarkRights {
  const { value } = useLive(() => data.call('bookmarkRights', channelId), ['channelBookmarks'], [channelId]);
  return value ?? NO_BOOKMARK_RIGHTS;
}

function useToastOutcome() {
  const shell = useShell();
  return useCallback(
    (r: BookmarkWriteOutcome) => {
      if (!r.ok) shell.toast(t(`web.bookmarks.failed.${r.reason}`), 'error');
    },
    [shell],
  );
}

const emojiName = (e: string) => e.replace(/^:|:$/g, '');

export function BookmarksBar({ channelId }: { channelId: string }) {
  const { value: list } = useLive(() => data.call('channelBookmarks', channelId), ['channelBookmarks'], [channelId]);
  const rights = useBookmarkRights(channelId);
  const [editing, setEditing] = useState<ChannelBookmarkRecord | 'new' | null>(null);
  const [menu, setMenu] = useState<{ bookmark: ChannelBookmarkRecord; x: number; y: number } | null>(null);
  const report = useToastOutcome();

  // The server's list on every open (the store already painted what it had).
  useEffect(() => {
    void data.call('refreshChannelBookmarks', channelId);
    setMenu(null);
    setEditing(null);
  }, [channelId]);

  const bookmarks = list ?? [];
  if (!bookmarks.length && editing === null) return null;

  return (
    <>
      {bookmarks.length ? (
        <nav className="bookmarks-bar" aria-label={t('web.bookmarks.bar')}>
          <div className="bookmarks-scroll">
            {bookmarks.map((b) => (
              <BookmarkChip
                key={b.id}
                bookmark={b}
                onMenu={(x, y) => (rights.edit || rights.delete || rights.order || b.type === 'link') && setMenu({ bookmark: b, x, y })}
              />
            ))}
          </div>
          {rights.add ? (
            <button type="button" className="icon-button bookmarks-add" title={t('web.bookmarks.add')} aria-label={t('web.bookmarks.add')} onClick={() => setEditing('new')}>
              <Icon name="plus" size={15} />
            </button>
          ) : null}
        </nav>
      ) : null}
      {menu ? (
        <BookmarkMenu
          x={menu.x}
          y={menu.y}
          bookmark={menu.bookmark}
          rights={rights}
          index={bookmarks.findIndex((b) => b.id === menu.bookmark.id)}
          count={bookmarks.length}
          onClose={() => setMenu(null)}
          onEdit={() => setEditing(menu.bookmark)}
          onMove={(to) => data.call('moveBookmark', channelId, menu.bookmark.id, to).then(report)}
          onDelete={() => data.call('deleteBookmark', channelId, menu.bookmark.id).then(report)}
        />
      ) : null}
      {editing ? <BookmarkDialog channelId={channelId} bookmark={editing === 'new' ? null : editing} onClose={() => setEditing(null)} /> : null}
    </>
  );
}

/** «Agregar un marcador» in the channel info panel — the way in while the bar is empty (hidden). */
export function AddBookmarkRow({ channelId }: { channelId: string }) {
  const rights = useBookmarkRights(channelId);
  const [open, setOpen] = useState(false);
  if (!rights.add) return null;
  return (
    <>
      <ListRow leading={<Icon name="bookmark" size={18} />} title={t('web.bookmarks.add')} trailing={<Icon name="plus" size={15} />} onClick={() => setOpen(true)} />
      {open ? <BookmarkDialog channelId={channelId} bookmark={null} onClose={() => setOpen(false)} /> : null}
    </>
  );
}

function BookmarkChip({ bookmark: b, onMenu }: { bookmark: ChannelBookmarkRecord; onMenu(x: number, y: number): void }) {
  // Touch: a long press opens the menu (iOS Safari fires no contextmenu); the click that follows is eaten.
  const press = useRef<{ timer: ReturnType<typeof setTimeout>; x: number; y: number } | null>(null);
  const opened = useRef(false);
  const cancelPress = () => {
    if (press.current) clearTimeout(press.current.timer);
    press.current = null;
  };
  const kind = b.type === 'file' ? AttachmentKind.classify(b.fileMime, b.fileName, 0, 0) : null;
  const href = b.type === 'link' ? b.linkUrl : mediaUrls.file(b.fileId, !(kind === 'image' || b.fileMime === 'application/pdf'));
  const label = b.displayName || (b.type === 'link' ? defaultBookmarkName(b.linkUrl) : b.fileName);
  return (
    <a
      className="bookmark-chip"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      title={b.type === 'link' ? b.linkUrl : b.fileName}
      onPointerDown={(e) => {
        if (e.pointerType !== 'touch') return;
        opened.current = false;
        const { clientX: x, clientY: y } = e;
        press.current = {
          x,
          y,
          timer: setTimeout(() => {
            opened.current = true;
            press.current = null;
            onMenu(x, y);
          }, 500),
        };
      }}
      onPointerMove={(e) => {
        if (press.current && Math.hypot(e.clientX - press.current.x, e.clientY - press.current.y) > 8) cancelPress();
      }}
      onPointerUp={cancelPress}
      onPointerCancel={cancelPress}
      onClick={(e) => {
        if (opened.current) {
          opened.current = false;
          e.preventDefault();
          return;
        }
        if (b.type !== 'file' || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        if (kind === 'image') {
          e.preventDefault();
          openLightbox([{ fileId: b.fileId, name: b.fileName, author: '', at: b.createAt, width: 0, height: 0, mimeType: b.fileMime }], 0);
        } else if (b.fileMime === 'application/pdf') {
          e.preventDefault();
          openPdf(b.fileId, b.fileName);
        }
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        if (opened.current) return; // the long press already opened it
        cancelPress();
        onMenu(e.clientX, e.clientY);
      }}
      onKeyDown={(e) => {
        if (e.key === 'ContextMenu' || (e.key === 'F10' && e.shiftKey)) {
          e.preventDefault();
          const r = e.currentTarget.getBoundingClientRect();
          onMenu(r.left, r.bottom + 4);
        }
      }}
    >
      <span className="bookmark-glyph" aria-hidden>
        {b.emoji ? <EmojiGlyph name={emojiName(b.emoji)} /> : <Icon name={b.type === 'file' ? 'file' : 'link'} size={13} />}
      </span>
      <span className="bookmark-name">{label}</span>
    </a>
  );
}

function BookmarkMenu({
  x,
  y,
  bookmark,
  rights,
  index,
  count,
  onClose,
  onEdit,
  onMove,
  onDelete,
}: {
  x: number;
  y: number;
  bookmark: ChannelBookmarkRecord;
  rights: BookmarkRights;
  index: number;
  count: number;
  onClose(): void;
  onEdit(): void;
  onMove(to: number): void;
  onDelete(): void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.querySelector<HTMLElement>('.menu-item')?.focus();
    const close = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && onClose();
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') return onClose();
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      e.preventDefault();
      const items = [...(ref.current?.querySelectorAll<HTMLElement>('.menu-item') ?? [])];
      const at = items.indexOf(document.activeElement as HTMLElement);
      items[(at + (e.key === 'ArrowDown' ? 1 : items.length - 1)) % items.length]?.focus();
    };
    const id = setTimeout(() => window.addEventListener('mousedown', close));
    window.addEventListener('keydown', key);
    return () => {
      clearTimeout(id);
      window.removeEventListener('mousedown', close);
      window.removeEventListener('keydown', key);
    };
  }, [onClose]);
  const act = (f: () => void) => () => {
    onClose();
    f();
  };
  const width = 220;
  const left = Math.max(8, Math.min(x, window.innerWidth - width - 8));
  return createPortal(
    <div ref={ref} className="popover bookmark-menu" role="menu" aria-label={tf('web.bookmarks.menu', bookmark.displayName)} style={{ position: 'fixed', left, top: Math.min(y, window.innerHeight - 220), width }}>
      {bookmark.type === 'link' ? (
        <button type="button" className="menu-item" role="menuitem" onClick={act(() => void navigator.clipboard?.writeText(bookmark.linkUrl).catch(() => undefined))}>
          <Icon name="copy" size={15} />
          {t('web.bookmarks.copyLink')}
        </button>
      ) : null}
      {rights.edit ? (
        <button type="button" className="menu-item" role="menuitem" onClick={act(onEdit)}>
          <Icon name="pencilLine" size={15} />
          {t('web.bookmarks.edit')}
        </button>
      ) : null}
      {rights.order && index > 0 ? (
        <button type="button" className="menu-item" role="menuitem" onClick={act(() => onMove(index - 1))}>
          <Icon name="chevronLeft" size={15} />
          {t('web.bookmarks.moveLeft')}
        </button>
      ) : null}
      {rights.order && index < count - 1 ? (
        <button type="button" className="menu-item" role="menuitem" onClick={act(() => onMove(index + 1))}>
          <Icon name="chevronRight" size={15} />
          {t('web.bookmarks.moveRight')}
        </button>
      ) : null}
      {rights.delete ? (
        <>
          <div className="menu-sep" />
          <button type="button" className="menu-item danger" role="menuitem" onClick={act(onDelete)}>
            <Icon name="trash" size={15} />
            {t('web.bookmarks.delete')}
          </button>
        </>
      ) : null}
    </div>,
    document.body,
  );
}

/** Add (a link) or edit (name, emoji, and the link of a link bookmark). */
export function BookmarkDialog({ channelId, bookmark, onClose }: { channelId: string; bookmark: ChannelBookmarkRecord | null; onClose(): void }) {
  const [url, setUrl] = useState(bookmark?.linkUrl ?? '');
  const [name, setName] = useState(bookmark?.displayName ?? '');
  const [emoji, setEmoji] = useState(bookmark?.emoji ?? '');
  const [problem, setProblem] = useState<string | null>(null);
  const report = useToastOutcome();
  const isFile = bookmark?.type === 'file';
  const submit = () => {
    const link = isFile ? null : normalizeBookmarkUrl(url);
    if (!isFile && !link) return setProblem(t('web.bookmarks.invalidLink'));
    const displayName = (name.trim() || (link ? defaultBookmarkName(link) : bookmark?.fileName ?? '')).slice(0, BOOKMARK_NAME_MAX);
    const e = emojiName(emoji.trim());
    onClose();
    const write = bookmark ? data.call('editBookmark', channelId, bookmark.id, displayName, link, e) : data.call('addLinkBookmark', channelId, displayName, link!, e);
    write.then(report, () => report({ ok: false, reason: 'network' }));
  };
  return createPortal(
    <Dialog
      title={t(bookmark ? 'web.bookmarks.editTitle' : 'web.bookmarks.addTitle')}
      onClose={onClose}
      width={420}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button kind="primary" onClick={submit}>
            {t('web.bookmarks.save')}
          </Button>
        </>
      }
    >
      <form
        noValidate
        className="bookmark-form"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        {!isFile ? (
          <label className="st-field">
            <span>{t('web.bookmarks.link')}</span>
            <input className="text-input" type="text" inputMode="url" autoCapitalize="off" autoCorrect="off" spellCheck={false} value={url} placeholder={t('web.bookmarks.linkPlaceholder')} onChange={(e) => (setUrl(e.target.value), setProblem(null))} autoFocus />
          </label>
        ) : null}
        <label className="st-field">
          <span>{t('web.bookmarks.name')}</span>
          <input className="text-input" value={name} maxLength={BOOKMARK_NAME_MAX} placeholder={t('web.bookmarks.namePlaceholder')} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="st-field">
          <span>{t('web.bookmarks.emoji')}</span>
          <input className="text-input" value={emoji} placeholder=":link:" onChange={(e) => setEmoji(e.target.value)} />
        </label>
        {problem ? (
          <p className="st-save st-save-error" role="alert">
            {problem}
          </p>
        ) : null}
        <button type="submit" hidden />
      </form>
    </Dialog>,
    document.body,
  );
}
